/**
 * PKCE (RFC 7636) and the one-time values that make an authorization-code
 * redirect safe for a public client.
 *
 * The console is a static SPA with no server and therefore no client secret,
 * which is exactly the case PKCE exists for: without it, anyone who intercepts
 * the authorization code can redeem it, because nothing else proves the
 * redeemer is the party that asked.
 */

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/** base64url, per RFC 7636 §A — no padding, URL-safe alphabet. */
function base64Url(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  for (const byte of view) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * A high-entropy verifier. 32 random bytes — 43 base64url characters, the
 * minimum RFC 7636 §4.1 permits and the length Keycloak documents.
 */
export function createCodeVerifier(): string {
  return base64Url(randomBytes(32));
}

/**
 * S256 ONLY. The spec also allows `plain`, where the challenge IS the verifier
 * — which defeats the mechanism entirely for anyone who saw the authorization
 * request. There is no reason to offer it and no fallback here.
 */
export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(digest);
}

/** An opaque, single-use value. Used for both `state` and `nonce`. */
export function createOpaqueValue(): string {
  return base64Url(randomBytes(16));
}

/**
 * Constant-time-ish comparison for the values returned through the redirect.
 *
 * `===` on a short opaque string is not a realistic timing oracle, but the
 * cost of not thinking about it is zero and the habit is worth keeping in the
 * one place a security console compares a secret.
 */
export function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
